import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck, FileText, CheckCircle, XCircle,
  AlertTriangle, Loader2, Calendar, Package,
  MapPin, User, Mail, Hash
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GRADE_CONFIG = {
  A: { color: 'from-green-400 to-green-600', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Excellent' },
  B: { color: 'from-blue-400 to-blue-600', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Good' },
  C: { color: 'from-yellow-400 to-yellow-600', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'Fair' },
  D: { color: 'from-orange-400 to-orange-600', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Poor' },
  E: { color: 'from-red-500 to-red-700', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Critical' },
};

const LOIQRValidation = () => {
  const { verificationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchValidation = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/public/loi-verification/qr-validate/${verificationId}`);
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || 'Verification not found');
        }
        setData(await resp.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchValidation();
  }, [verificationId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#2B4B6A] mx-auto mb-3" />
          <p className="text-gray-500">Validating LOI...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verification Not Found</h1>
          <p className="text-gray-500 mb-6">
            This QR code does not match any verified LOI in our system. The document may not have been processed through Trados.
          </p>
          <a
            href="/verify-loi"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#2B4B6A] text-white rounded-xl font-medium hover:bg-[#1e3a55] transition"
          >
            <ShieldCheck className="w-4 h-4" />
            Verify an LOI
          </a>
        </motion.div>
      </div>
    );
  }

  const grade = data.grade || '?';
  const config = GRADE_CONFIG[grade] || GRADE_CONFIG.C;
  const verifiedDate = data.verified_date ? new Date(data.verified_date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : 'Unknown';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-lg mx-auto pt-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Trados Logo */}
          <div className="flex flex-col items-center mb-6">
            <img
              src="https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified-sqaured.jpg"
              alt="Trados Verified"
              style={{ width: 250 }}
            />
          </div>

          {/* Validation status */}
          <div className={`${data.compliance ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'} border rounded-2xl p-6 text-center mb-6`}>
            <div className={`w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center ${data.compliance ? 'bg-green-100' : 'bg-orange-100'}`}>
              {data.compliance ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-orange-600" />
              )}
            </div>
            <h1 className={`text-xl font-bold mb-1 ${data.compliance ? 'text-green-800' : 'text-orange-800'}`}>
              {data.compliance ? 'Verified LOI Document' : 'LOI Verified — Concerns Found'}
            </h1>
            <p className={`text-sm ${data.compliance ? 'text-green-600' : 'text-orange-600'}`}>
              {data.compliance
                ? 'This document has been verified through the Trados LOI Verification Engine and passed compliance.'
                : 'This document was analyzed by Trados but did not pass full compliance.'}
            </p>
          </div>

          {/* Grade card */}
          <div className={`${config.bg} ${config.border} border rounded-2xl p-5 mb-6`}>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center shadow-md flex-shrink-0`}>
                <span className="text-2xl font-black text-white">{grade}</span>
              </div>
              <div className="flex-1">
                <h2 className={`text-lg font-bold ${config.text}`}>{config.label}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-2xl font-bold text-gray-900">{data.score}%</span>
                  <span className="text-sm text-gray-500">{data.passed_checks}/{data.total_checks} checks passed</span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-200 mt-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${config.color}`}
                    style={{ width: `${data.score}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Document details */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-sm">Document Details</h3>
            </div>
            <div className="divide-y divide-gray-100">
              <DetailRow icon={FileText} label="Original File" value={data.original_filename} />
              <DetailRow icon={Calendar} label="Verified Date" value={verifiedDate} />
              {data.product && <DetailRow icon={Package} label="Product / Commodity" value={data.product} />}
              {data.quantity && <DetailRow icon={Hash} label="Quantity" value={data.quantity} />}
              {data.port_of_destination && <DetailRow icon={MapPin} label="Port of Destination" value={data.port_of_destination} />}
              {data.buyer_name && <DetailRow icon={User} label="Buyer" value={data.buyer_name} />}
              {data.buyer_email && <DetailRow icon={Mail} label="Email" value={data.buyer_email} />}
            </div>
          </div>

          {/* Verification ID */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400 font-mono">
              Verification ID: {verificationId}
            </p>
          </div>

          {/* CTA */}
          <div className="mt-6 bg-gradient-to-r from-[#2B4B6A] to-[#3a6b8a] rounded-2xl p-6 text-center">
            <h3 className="text-lg font-bold text-white mb-2">
              Need to verify your own LOI?
            </h3>
            <p className="text-white/70 text-sm mb-4">
              Upload any Letter of Intent and get an instant AI-powered analysis.
            </p>
            <a
              href="/verify-loi"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#2B4B6A] rounded-xl font-medium hover:bg-gray-100 transition text-sm"
            >
              <ShieldCheck className="w-4 h-4" />
              Verify LOI Free
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 px-5 py-3">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm text-gray-800 break-words">{value}</p>
      </div>
    </div>
  );
};

export default LOIQRValidation;
